import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const WEEKDAYS = ["H", "K", "Sze", "Cs", "P", "Szo", "V"];

function localDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatMonthTitle(year, month) {
  const d = new Date(year, month, 1);
  return d.toLocaleDateString("hu-HU", { year: "numeric", month: "long" });
}

function formatSlotTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" });
}

function formatSlotRange(slot) {
  const from = formatSlotTime(slot.start_at);
  const to = slot.end_at
    ? formatSlotTime(slot.end_at)
    : formatSlotTime(new Date(new Date(slot.start_at).getTime() + (slot.duration_min || 60) * 60000).toISOString());
  return `${from}–${to}`;
}

function formatSlotDetail(slot) {
  try {
    const d = new Date(slot.start_at);
    if (Number.isNaN(d.getTime())) return formatSlotRange(slot);
    const day = d.toLocaleDateString("hu-HU", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    return `${day}, ${formatSlotRange(slot)}`;
  } catch {
    return formatSlotRange(slot);
  }
}

function statusLabel(status) {
  if (status === "reserved") return "Foglalt";
  if (status === "completed") return "Kész";
  if (status === "open") return "Szabad";
  return status;
}

function slotPillClass(status) {
  if (status === "reserved") return "bg-violet-500/25 text-violet-100";
  if (status === "completed") return "bg-slate-500/25 text-slate-300";
  return "bg-emerald-500/20 text-emerald-100";
}

function buildMonthCells(year, month) {
  const padStart = (new Date(year, month, 1).getDay() + 6) % 7;
  const cells = [];
  let day = 1 - padStart;
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(year, month, day);
    cells.push({ date, inMonth: date.getMonth() === month });
    day += 1;
  }
  return cells;
}

function isSameDay(a, b) {
  return localDateKey(a) === localDateKey(b);
}

function isPastDay(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

export function SellerViewingCalendarPanel() {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(() => localDateKey(today));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timeFrom, setTimeFrom] = useState("10:00");
  const [timeTo, setTimeTo] = useState("12:00");
  const [note, setNote] = useState("");

  const monthRange = useMemo(() => {
    const start = new Date(viewYear, viewMonth, 1);
    const end = new Date(viewYear, viewMonth + 1, 0);
    return { start, end };
  }, [viewYear, viewMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = localDateKey(monthRange.start);
      const to = localDateKey(monthRange.end);
      const r = await api.get("/my/viewing-calendar/slots", { params: { from, to } });
      setSlots(r.data.slots || []);
    } catch {
      toast.error("Naptár betöltése sikertelen");
    } finally {
      setLoading(false);
    }
  }, [monthRange]);

  useEffect(() => {
    load();
  }, [load]);

  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const key = localDateKey(new Date(s.start_at));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    for (const [, list] of map) {
      list.sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)));
    }
    return map;
  }, [slots]);

  const monthCells = useMemo(() => buildMonthCells(viewYear, viewMonth), [viewYear, viewMonth]);

  const selectedSlots = useMemo(
    () => slotsByDay.get(selectedDate) || [],
    [slotsByDay, selectedDate]
  );

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const pickDay = (date, inMonth) => {
    if (!inMonth) {
      setViewYear(date.getFullYear());
      setViewMonth(date.getMonth());
    }
    setSelectedDate(localDateKey(date));
  };

  const addSlot = async (e) => {
    e.preventDefault();
    if (!selectedDate || !timeFrom || !timeTo) {
      toast.error("Add meg a tól–ig időt");
      return;
    }
    if (isPastDay(new Date(`${selectedDate}T12:00:00`))) {
      toast.error("Múltbeli napra nem vehetsz fel idősávot");
      return;
    }
    const start_at = new Date(`${selectedDate}T${timeFrom}:00`);
    const end_at = new Date(`${selectedDate}T${timeTo}:00`);
    if (Number.isNaN(start_at.getTime()) || Number.isNaN(end_at.getTime())) {
      toast.error("Érvénytelen időpont");
      return;
    }
    if (end_at <= start_at) {
      toast.error("Az „ig” időpontnak későbbinek kell lennie, mint a „tól”");
      return;
    }
    setSaving(true);
    try {
      await api.post("/my/viewing-calendar/slots", {
        start_at: start_at.toISOString(),
        end_at: end_at.toISOString(),
        note: note.trim(),
      });
      toast.success("Idősáv felvéve");
      setNote("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  const removeSlot = async (id) => {
    if (!window.confirm("Törlöd ezt a szabad idősávot?")) return;
    try {
      await api.delete(`/my/viewing-calendar/slots/${id}`);
      toast.success("Törölve");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Törlés sikertelen");
    }
  };

  return (
    <div data-testid="seller-viewing-calendar" className="flex flex-col flex-1 min-h-0">
      <div className="px-5 py-3 border-b border-white/10 flex items-start gap-3 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
          <CalendarDays className="w-4 h-4 text-emerald-300" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-base font-light text-white">Megtekintés-naptár</h2>
          <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
            Kattints egy napra, add hozzá a szabad idősávokat — az AI ide foglal érdeklődőket.
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        <div className="flex-1 min-w-0 flex flex-col p-3 min-h-0 border-b lg:border-b-0 lg:border-r border-white/10">
          <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
            <button
              type="button"
              data-testid="calendar-prev-month"
              onClick={() => shiftMonth(-1)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
              aria-label="Előző hónap"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div
              data-testid="calendar-month-title"
              className="text-sm font-medium text-white capitalize"
            >
              {formatMonthTitle(viewYear, viewMonth)}
            </div>
            <button
              type="button"
              data-testid="calendar-next-month"
              onClick={() => shiftMonth(1)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
              aria-label="Következő hónap"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-px mb-1 shrink-0">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="text-center text-[10px] uppercase tracking-wide text-slate-500 py-1"
              >
                {w}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Betöltés…
            </div>
          ) : (
            <div
              data-testid="calendar-month-grid"
              className="grid grid-cols-7 gap-1 flex-1 auto-rows-fr min-h-[220px]"
            >
              {monthCells.map(({ date, inMonth }) => {
                const key = localDateKey(date);
                const daySlots = slotsByDay.get(key) || [];
                const selected = key === selectedDate;
                const past = inMonth && isPastDay(date);
                const openCount = daySlots.filter((s) => s.status === "open").length;
                const reservedCount = daySlots.filter((s) => s.status === "reserved").length;

                return (
                  <button
                    key={`${key}-${inMonth}`}
                    type="button"
                    data-testid={`calendar-day-${key}`}
                    onClick={() => pickDay(date, inMonth)}
                    className={`min-h-[52px] rounded-lg border text-left p-1 flex flex-col transition-colors ${
                      selected
                        ? "border-emerald-400/50 bg-emerald-500/10 ring-1 ring-emerald-400/30"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                    } ${!inMonth ? "opacity-35" : ""} ${past ? "opacity-50" : ""}`}
                  >
                    <span
                      className={`text-xs font-medium ${
                        selected ? "text-emerald-100" : "text-slate-300"
                      }`}
                    >
                      {date.getDate()}
                    </span>
                    <div className="flex-1 mt-0.5 space-y-0.5 overflow-hidden">
                      {daySlots.slice(0, 2).map((s) => (
                        <div
                          key={s.id}
                          className={`text-[9px] leading-tight truncate rounded px-0.5 ${slotPillClass(s.status)}`}
                        >
                          {formatSlotRange(s)}
                        </div>
                      ))}
                      {daySlots.length > 2 ? (
                        <div className="text-[9px] text-slate-500">+{daySlots.length - 2}</div>
                      ) : null}
                    </div>
                    {(openCount > 0 || reservedCount > 0) && daySlots.length <= 2 ? (
                      <div className="flex gap-0.5 mt-auto pt-0.5">
                        {openCount > 0 ? (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Szabad" />
                        ) : null}
                        {reservedCount > 0 ? (
                          <span className="w-1.5 h-1.5 rounded-full bg-violet-400" title="Foglalt" />
                        ) : null}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-slate-500 shrink-0">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400" /> Szabad
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-violet-400" /> AI / érdeklődő
            </span>
          </div>
        </div>

        <div className="w-full lg:w-[min(100%,280px)] shrink-0 flex flex-col min-h-[180px] max-h-[45%] lg:max-h-none">
          <div className="px-4 py-2 border-b border-white/10 shrink-0">
            <div className="text-xs text-slate-500">Kiválasztott nap</div>
            <div className="text-sm text-white capitalize">
              {selectedDate
                ? new Date(`${selectedDate}T12:00:00`).toLocaleDateString("hu-HU", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "—"}
            </div>
            <input type="hidden" data-testid="calendar-add-date" value={selectedDate} readOnly />
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
            {selectedSlots.length === 0 ? (
              <p className="text-xs text-slate-500">Ezen a napon még nincs idősáv.</p>
            ) : (
              selectedSlots.map((s) => (
                <div
                  key={s.id}
                  data-testid={`calendar-slot-${s.id}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-2.5 py-2"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white">{formatSlotDetail(s)}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{statusLabel(s.status)}</div>
                      {s.note ? <div className="text-xs text-slate-400 mt-0.5">{s.note}</div> : null}
                      {s.status === "reserved" && s.buyer_label ? (
                        <div className="text-xs text-violet-300 mt-1">{s.buyer_label}</div>
                      ) : null}
                    </div>
                    {s.status === "open" ? (
                      <button
                        type="button"
                        data-testid={`calendar-slot-delete-${s.id}`}
                        onClick={() => removeSlot(s.id)}
                        className="shrink-0 p-1 rounded-lg text-slate-500 hover:text-red-300 hover:bg-red-500/10"
                        title="Törlés"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>

          <form
            className="p-3 border-t border-white/10 shrink-0 space-y-2"
            onSubmit={addSlot}
            data-testid="calendar-add-form"
          >
            <div className="text-[11px] text-slate-500">Új szabad idősáv ezen a napon</div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <label className="block">
                <span className="text-[10px] text-slate-500 mb-0.5 block">Tól</span>
                <input
                  type="time"
                  data-testid="calendar-add-time-from"
                  value={timeFrom}
                  onChange={(e) => setTimeFrom(e.target.value)}
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-2 py-1.5 text-sm text-white"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-500 mb-0.5 block">Ig</span>
                <input
                  type="time"
                  data-testid="calendar-add-time-to"
                  value={timeTo}
                  onChange={(e) => setTimeTo(e.target.value)}
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-2 py-1.5 text-sm text-white"
                />
              </label>
            </div>
            <input
              type="text"
              placeholder="Megjegyzés (opc.)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-2 py-1.5 text-sm text-white placeholder:text-slate-500"
            />
            <button
              type="submit"
              disabled={saving || isPastDay(new Date(`${selectedDate}T12:00:00`))}
              data-testid="calendar-add-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Hozzáadás
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
